import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@headerhawk/ui";
import {
  useOnboardingStore,
  type OnboardingStep,
} from "@/lib/stores/onboarding-store";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";

interface StepContainerProps {
  step: OnboardingStep;
  title: string;
  description?: string;
  children: React.ReactNode;
  onComplete?: () => void | Promise<void>;
  isCompleting?: boolean;
  canContinue?: boolean;
  continueLabel?: string;
  showBack?: boolean;
}

export function StepContainer({
  step,
  title,
  description,
  children,
  onComplete,
  isCompleting: externalIsCompleting = false,
  canContinue = true,
  continueLabel = "Continue",
  showBack = true,
}: StepContainerProps) {
  const navigate = useNavigate();
  const { completeStep, getNextStep, getPreviousStep } = useOnboardingStore();
  const [internalIsCompleting, setInternalIsCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCompleting = externalIsCompleting || internalIsCompleting;
  const previousStep = getPreviousStep(step);
  const nextStep = getNextStep(step);

  // Track when user arrives at this onboarding step
  useEffect(() => {
    trackEvent(AnalyticsEvents.ONBOARDING_STEP, { step });
  }, [step]);

  const handleBack = () => {
    if (previousStep) {
      navigate(`/onboarding/${previousStep}`);
    }
  };

  const handleContinue = async () => {
    setError(null);
    setInternalIsCompleting(true);

    try {
      if (onComplete) {
        await onComplete();
      }
      completeStep(step);
      if (nextStep) {
        navigate(`/onboarding/${nextStep}`);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      // Also on the success path. The onboarding routes are lazy, and while
      // the next step's chunk loads the Suspense boundary hides this step
      // rather than unmounting it - so a browser Back in that window brings
      // back a live step whose Continue button is stuck on "Please wait...".
      // Once the step really is unmounted this is a no-op.
      setInternalIsCompleting(false);
    }
  };

  const isFinalStep = !nextStep;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
        {description && <p className="text-muted-foreground">{description}</p>}
      </div>

      <div className="py-4">{children}</div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center justify-between pt-4">
        <div>
          {showBack && previousStep && (
            <Button
              variant="ghost"
              onClick={handleBack}
              disabled={isCompleting}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          )}
        </div>
        <Button
          onClick={handleContinue}
          disabled={!canContinue || isCompleting}
        >
          {isCompleting ? (
            "Please wait..."
          ) : isFinalStep ? (
            "Go to Dashboard"
          ) : (
            <>
              {continueLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
